// Janela de atendimento da Meta, em um lugar só.
//
// Regra padrão: 24h contadas da última mensagem do cliente. Grupo não tem
// `lastDateMsgClient` (é exclusivo de conversa 1:1) e usa `groupServiceWindowOpenedAt`;
// Instagram e Messenger guardam a última mensagem do cliente dentro do objeto do
// usuário da plataforma.
//
// Click-to-WhatsApp: quando o contato chega clicando num anúncio, a conversa é uma
// "free entry point conversation" e a Meta concede 72h. Esse prazo CONVIVE com as 24h
// da última mensagem do cliente — vale o que expirar mais tarde, porque uma mensagem
// nova do cliente no fim das 72h abre 24h próprias. Quem carimba `ctwaReferralAt` no
// chat é o lais-webhook, a partir do `referral` que a Meta manda na primeira inbound.
//
// Antes desta função a regra das 24h estava reimplementada inline no ChatBottomBar, no
// item da lista, no contador e no modal de transferência — e cada cópia conhecia um
// subconjunto diferente dos campos.
export const WINDOW_MS = 24 * 60 * 60 * 1000;
export const CTWA_WINDOW_MS = 72 * 60 * 60 * 1000;

export const isWhatsappBusiness = (chat) => chat?.canal === 'whatsapp_business';

const toTime = (value) => {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
};

export const getServiceWindowReference = (chat) => {
    if (isWhatsappBusiness(chat)) {
        return chat?.isGroup === true ? chat?.groupServiceWindowOpenedAt : chat?.lastDateMsgClient;
    }
    if (chat?.canal === 'instagram') return chat?.instagramUser?.lastDateMessageUser;
    if (chat?.canal === 'messenger') return chat?.messengerUser?.lastDateMessageUser;
    return null;
};

// Prazo da janela de anúncio, ou null quando não houve anúncio ou quando a Meta já
// recusou um envio com 131047 (`ctwaWindowClosedAt`) antes das 72h teóricas — as 72h
// são um teto, não uma garantia: a free entry point pode ter sido consumida antes.
// Não existe CTWA em grupo.
export const getCtwaDeadline = (chat) => {
    if (!isWhatsappBusiness(chat) || chat?.isGroup === true) return null;
    if (chat?.ctwaWindowClosedAt) return null;
    const referral = toTime(chat?.ctwaReferralAt);
    return referral === null ? null : referral + CTWA_WINDOW_MS;
};

export const getWindowExpiresAt = (chat) => {
    const reference = toTime(getServiceWindowReference(chat));
    const base = reference === null ? null : reference + WINDOW_MS;
    const ctwa = getCtwaDeadline(chat);
    if (base === null && ctwa === null) return null;
    return new Date(Math.max(base === null ? 0 : base, ctwa === null ? 0 : ctwa));
};

export const hasServiceWindowReference = (chat) => getWindowExpiresAt(chat) !== null;

export const isServiceWindowExpired = (chat, now = Date.now()) => {
    const expiresAt = getWindowExpiresAt(chat);
    return expiresAt ? now > expiresAt.getTime() : false;
};

export const shouldBlockWhatsAppBusiness = (chat) =>
    isWhatsappBusiness(chat) && (!hasServiceWindowReference(chat) || isServiceWindowExpired(chat));

// Qual prazo está mandando: decide entre falar "24h" ou "anúncio · 72h" na tela.
export const isCtwaWindowGoverning = (chat) => {
    const ctwa = getCtwaDeadline(chat);
    const expiresAt = getWindowExpiresAt(chat);
    return !!ctwa && !!expiresAt && expiresAt.getTime() === ctwa;
};

export const getWindowTimeLeft = (chat, now = Date.now()) => {
    const expiresAt = getWindowExpiresAt(chat);
    if (!expiresAt) return null;
    const ms = expiresAt.getTime() - now;
    if (ms <= 0) return { ms: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    return {
        ms,
        hours: Math.floor(ms / (60 * 60 * 1000)),
        minutes: Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000)),
        seconds: Math.floor((ms % (60 * 1000)) / 1000),
        expired: false,
    };
};

// Horas restantes com sinal (negativo = já expirou) ou null quando não há janela.
export const getWindowRemainingHours = (chat, now = Date.now()) => {
    const expiresAt = getWindowExpiresAt(chat);
    return expiresAt === null ? null : (expiresAt.getTime() - now) / (60 * 60 * 1000);
};
